import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, Users, LayoutTemplate, Send, ShieldCheck, ArrowRight, Check } from "lucide-react";
import { WhatsAppSupport } from "../WhatsAppSupport";
const black = "#111111";
const BASE = process.env.REACT_APP_BACKEND_URL;

function PlatformContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2 || !form.email.trim()) {
      setErr("Completeaza numele si emailul.");
      return;
    }
    setState("sending");
    try {
      await axios.post(`${BASE}/api/public/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      setState("done");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Trimiterea a esuat. Reincearca.");
      setState("error");
    }
  };

  const inputCls = "w-full px-3 py-2 rounded-md border border-gray-300";

  if (state === "done") {
    return (
      <div className="border border-gray-300 rounded-lg p-6 text-center" data-testid="home-contact-success">
        <Check className="w-7 h-7 mx-auto mb-2" style={{ color: black }} />
        <p className="font-bold" style={{ fontSize: "14px" }}>Mesaj trimis</p>
        <p className="mt-1">Iti multumim. Te contactam cat mai curand.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-gray-300 rounded-lg p-6 space-y-3" data-testid="home-contact-form" style={{ fontSize: "13px" }}>
      <input value={form.name} onChange={set("name")} placeholder="Nume" required
        data-testid="home-contact-name" className={inputCls} />
      <input value={form.email} onChange={set("email")} type="email" placeholder="Email" required
        data-testid="home-contact-email" className={inputCls} />
      <input value={form.phone} onChange={set("phone")} placeholder="Telefon"
        data-testid="home-contact-phone" className={inputCls} />
      <textarea value={form.message} onChange={set("message")} placeholder="Mesaj" rows={4}
        data-testid="home-contact-message" className={inputCls} />
      {err && <p className="text-red-600" data-testid="home-contact-error">{err}</p>}
      <button type="submit" disabled={state === "sending"} data-testid="home-contact-submit"
        className="px-5 py-2.5 rounded-md text-white disabled:opacity-60" style={{ backgroundColor: black }}>
        {state === "sending" ? "Se trimite..." : "Trimite mesajul"}
      </button>
    </form>
  );
}

export default function Home() {
  return (
    <div
      style={{ fontFamily: "Arial, sans-serif", fontSize: "12px", color: black, backgroundColor: "#ffffff" }}
      className="min-h-screen"
      data-testid="home-page"
    >
      <header className="border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md border border-gray-300 flex items-center justify-center">
              <Mail className="w-4 h-4" style={{ color: black }} />
            </div>
            <span style={{ fontSize: "18px" }} className="font-bold">YVOLA</span>
          </div>
          <nav className="flex items-center gap-4">
            <Link to="/login" data-testid="home-login-link" className="hover:underline">Autentificare</Link>
            <Link
              to="/signup"
              data-testid="home-signup-top"
              className="px-3 py-1.5 rounded-md text-white"
              style={{ backgroundColor: black }}
            >
              Inregistreaza firma
            </Link>
          </nav>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-16 grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <h1 style={{ fontSize: "34px", lineHeight: "1.15" }} className="font-bold">
            Iti aducem potentiali clienti (B2B) si poti sa-i gestionezi intr-un singur loc
          </h1>
          <p style={{ fontSize: "14px" }} className="mt-4">
            YVOLA este un serviciu multitenant unde iti inregistrezi firma, iti creezi automat pagina publica de
            prezentare, iar noi cautam firme care au sanse sa devina clientii tai. Le afisam in sectiunea ta si le
            poti contacta direct din aplicatie.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Link
              to="/signup"
              data-testid="home-signup-hero"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white"
              style={{ backgroundColor: black, fontSize: "13px" }}
            >
              Creeaza cont firma <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              data-testid="home-login-hero"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-gray-300"
              style={{ fontSize: "13px" }}
            >
              Am deja cont
            </Link>
          </div>
        </div>
        <img
          src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxzbWFsbCUyMGJ1c2luZXNzJTIwdGVhbSUyMHdvcmtpbmclMjBsYXB0b3B8ZW58MHx8fHwxNzg4NjA4MzkxfDA&ixlib=rb-4.1.0&q=85&w=1200"
          alt="Echipa firma lucrand"
          data-testid="home-hero-image"
          className="w-full h-72 object-cover rounded-lg border border-gray-200"
        />
      </section>

      <section className="border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Users, t: "Contacte si lead-uri", d: "Importa contacte din CSV, elimini duplicatele si aduni lead-uri de pe pagina ta publica." },
            { icon: LayoutTemplate, t: "Pagina publica proprie", d: "Iti creezi manual pagina de prezentare cu text, poza principala si galerie foto." },
            { icon: Send, t: "Campanii email", d: "Creezi sabloane, trimiti campanii si urmaresti livrarea catre clientii tai." },
            { icon: ShieldCheck, t: "Date separate pe firma", d: "Fiecare firma are propriul cont de administrator si propriile date izolate." },
          ].map((f, i) => (
            <div key={i} data-testid={`home-feature-${i}`}>
              <div className="w-9 h-9 rounded-md border border-gray-300 flex items-center justify-center mb-3">
                <f.icon className="w-4 h-4" style={{ color: black }} />
              </div>
              <h3 style={{ fontSize: "14px" }} className="font-bold">{f.t}</h3>
              <p className="mt-1.5">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <h2 style={{ fontSize: "20px" }} className="font-bold">Cum functioneaza in 9 pasi</h2>
          <div className="mt-8 grid gap-6 lg:grid-cols-2 items-start">
            <ol className="space-y-3">
              {[
                "Iti creezi user pe firma proprie.",
                "Confirmi mailul ca sa se activeze contul.",
                "Intri in cont si adaugi foto si descrierea afacerii.",
                "Se creeaza automat site-ul pentru afacerea ta.",
                "Setezi numarul de contacte calitative dorite.",
                "Cautam firme care au sanse sa devina clientii tai.",
                "Iti afisam in sectiunea ta pe cei care doresc sa colaboreze cu tine.",
                "Ii contactezi direct din aplicatie sau pe alte canale: telefon, WhatsApp, mail personal.",
                "Gestionezi lead-urile in sectiunea CRM prin notite, activitati si altele.",
              ].map((s, i) => (
                <li key={i} className="flex gap-3" data-testid={`home-step-${i}`}>
                  <span
                    className="w-6 h-6 rounded-full text-white flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: black, fontSize: "12px" }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ fontSize: "14px" }}>{s}</span>
                </li>
              ))}
            </ol>
            <img
              src="https://images.unsplash.com/photo-1745847768380-2caeadbb3b71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwJTIwbWVldGluZ3xlbnwwfHx8fDE3ODg2MDgzOTF8MA&ixlib=rb-4.1.0&q=85&w=1200"
              alt="Colaborare intre firme"
              data-testid="home-steps-image"
              className="w-full h-full max-h-96 object-cover rounded-lg border border-gray-200"
            />
          </div>
          <Link
            to="/signup"
            data-testid="home-signup-bottom"
            className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white"
            style={{ backgroundColor: black, fontSize: "13px" }}
          >
            Incepe acum <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="border-t border-gray-200" data-testid="home-contact-section">
        <div className="max-w-5xl mx-auto px-6 py-14 grid gap-8 lg:grid-cols-2 items-start">
          <div>
            <h2 style={{ fontSize: "20px" }} className="font-bold">Contacteaza-ne</h2>
            <p className="mt-3" style={{ fontSize: "14px" }}>
              Ai intrebari despre YVOLA sau vrei o oferta pentru firma ta? Lasa-ne un mesaj si te contactam.
            </p>
          </div>
          <PlatformContactForm />
        </div>
      </section>

      <footer className="border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <span>YVOLA</span>
          <span>yvola.eu</span>
        </div>
      </footer>
      <WhatsAppSupport />
    </div>
  );
}
